import {
  FirebaseAuthentication,
  ProviderId,
} from '@capacitor-firebase/authentication'
import { FirebaseError } from 'firebase/app'
import {
  browserPopupRedirectResolver,
  confirmPasswordReset,
  EmailAuthProvider,
  getAdditionalUserInfo,
  getRedirectResult,
  IdTokenResult,
  linkWithCredential,
  linkWithPopup,
  OAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  TwitterAuthProvider,
  unlink,
  updateEmail as updateAuthEmail,
  updatePassword,
  User as FBUser,
  UserCredential,
  verifyPasswordResetCode,
} from 'firebase/auth'
import get from 'lodash/get'
import { createContext, useContext, useEffect, useState } from 'react'

import { AuthAdapterFn, messageRef, User, UserClaims } from '@/core'
import { getAuth } from '@/lib/firebase'

const AuthContext = createContext<ReturnType<typeof useAuthUser>>({
  loading: false,
  user: null,
  error: null,
  setUser: () => null,
  userClaims: null,
  setUserClaims: () => null,
})

export const adaptFirebaseAuth: AuthAdapterFn<{}, User | null> = () => {
  const useCurrentUser = () => useContext(AuthContext)

  return {
    useAuth() {
      const { user, loading, setUser, userClaims, setUserClaims } =
        useCurrentUser()

      return {
        user,
        isAuthenticated: !!user,
        loading,
        userClaims,
        // SNS認証のみによるアカウントかどうか
        isBoundWithSNS:
          !!user?.providers &&
          user?.providers.length === 1 &&
          (user?.providers[0].providerId === ProviderId.TWITTER ||
            user?.providers[0].providerId === ProviderId.APPLE),
        hasPasswordLogin: !!user?.providers?.some(
          provider => provider?.providerId === ProviderId.PASSWORD
        ),
        // 匿名ユーザーも手段ゼロになるため、本ログイン済みのものだけを対象にする
        hasNoLoginProvider: !!user?.isSigned && user.providers?.length === 0,
        reauthenticateCurrentUserWithPassword: async (password: string) => {
          // silent return すると呼出元が成功扱いで完了 modal を出してしまう。
          if (!user?.email) {
            throw new Error(
              'reauthenticateCurrentUserWithPassword: user.email is null'
            )
          }
          const refreshed = await reauthenticate(user.email, password)
          setUser(transformUser(refreshed.user))
        },
        loginWithEmail: (email, password, callback) => {
          return signInWithEmailAndPassword(getAuth(), email, password).then(
            async ({ user }) => {
              if (callback) {
                await callback()
              }
              const transformedUser = transformUser(user)
              setUser(transformedUser)
              return transformedUser
            }
          )
        },
        getUserClaims: async () => {
          const transformedUserClaims = await getUserClaims()
          setUserClaims(transformedUserClaims)
          return transformedUserClaims
        },
        logout: async () => {
          await signOut(getAuth())
          setUserClaims(null)
          setUser(null)
        },
        reloadUser: async () => {
          // currentUser 不在で setUser(null) すると強制ログアウト状態になるため、
          // サインアウト判定は onAuthStateChanged に委ねる (docs/utoniq-app/auth.md)。
          const currentUser = getAuth().currentUser
          if (!currentUser) return null
          await currentUser.reload()
          const transformed = transformUser(currentUser)
          setUser(transformed)
          // 戻り値で最新 user を返すのは React state の closure を待たない呼出側のため。
          return transformed
        },
        signInAnonymously: async () => {
          const auth = getAuth()
          return signInAnonymously(auth).then(({ user }) => {
            const transformedUser = transformUser(user)
            setUser(transformedUser)
            return transformedUser
          })
        },
        signInWithCustomToken: async (customToken: string) => {
          const { user } = await signInWithCustomToken(getAuth(), customToken)
          setUser(transformUser(user))
        },
        signInWithApple: async callback => {
          /**
           * NOTE: CapacitorのOAuthログインメソッドでは、credentialだけ取ってくることができないため、匿名ログインユーザーと本ログインユーザーを連結することができない。
           * そのため、カート機能においては、本ログイン時にAPIで匿名ユーザーを削除し、擬似的に連結している。
           * https://app.clickup.com/t/860t2bcy2
           */
          // 1. Create credentials on the native layer.
          // scopes はネイティブ側の ASAuthorizationAppleIDProvider で必要。
          // OAuthProvider.addScope を後段で呼んでも idToken は再発行されない (no-op) ため、
          // ここで指定しないと初回 sign-in 時に email が idToken に含まれない。
          const result = await FirebaseAuthentication.signInWithApple({
            skipNativeAuth: true,
            scopes: ['email', 'name'],
          })
          // 2. Sign in on the web layer using the id token and nonce
          const provider = new OAuthProvider(ProviderId.APPLE)
          const credential = provider.credential({
            idToken: result.credential?.idToken,
            rawNonce: result.credential?.nonce,
          })
          const auth = getAuth()
          const { user } = await signInWithCredential(auth, credential)
          if (callback) {
            await callback()
          }
          // @ts-ignore
          const transformedUser = transformUser(user)
          setUser(transformedUser)
        },
        signInWithTwitter: async callback => {
          /**
           * NOTE: CapacitorのOAuthログインメソッドでは、credentialだけ取ってくることができないため、匿名ログインユーザーと本ログインユーザーを連結することができない。
           * そのため、カート機能においては、本ログイン時にAPIで匿名ユーザーを削除し、擬似的に連結している。
           * https://app.clickup.com/t/860t2bcy2
           */
          // 1. Create credentials on the native layer
          const result = await FirebaseAuthentication.signInWithTwitter({
            skipNativeAuth: false,
          })
          // 2. Sign in on the web layer using the access token and secret
          const credential = TwitterAuthProvider.credential(
            result.credential?.accessToken || '',
            result.credential?.secret || ''
          )
          const auth = getAuth()
          const { user } = await signInWithCredential(auth, credential)
          if (callback) {
            await callback()
          }
          // @ts-ignore
          const transformedUser = transformUser({
            ...user,
            displayName:
              user?.displayName ??
              (result.additionalUserInfo?.profile?.name as string) ??
              '',
            email: result.additionalUserInfo?.profile?.email as string,
            photoURL: user?.photoURL?.replace('_normal', '') ?? '',
          })
          setUser(transformedUser)
          return {
            providerId: 'twitter',
            user: transformedUser,
            credential: {
              oauthToken: credential?.accessToken,
              oauthTokenSecret: credential?.secret,
            },
          }
        },
        linkEmail: async (email, password) => {
          const credential = EmailAuthProvider.credential(email, password)

          const user = await getUser()

          if (!user) throw new Error('user not found')

          await reauthenticateBySocialLogin()
          return linkWithCredential(user, credential).then(({ user }) => {
            const transformedUser = transformUser(user)
            setUser(transformedUser)
          })
        },
        unlinkEmail: async () => {
          const { providerId } = new EmailAuthProvider()

          const user = await getUser()

          if (!user) throw new Error('user not found')

          return unlink(user, providerId).then(user => {
            const transformedUser = transformUser(user)
            setUser(transformedUser)
          })
        },
        linkTwitter: async () => {
          const user = await getUser()
          if (!user) throw new Error('user not found')
          const res = await FirebaseAuthentication.linkWithTwitter({
            skipNativeAuth: false,
          })
          // credentialが取得できない場合エラー
          if (!res?.credential) throw new Error('authenticationFailed')
          const transformedUser = transformUser(await getUser())
          setUser(transformedUser)
          const { additionalUserInfo: info, credential } = res
          return {
            providerId: 'twitter',
            user: transformedUser,
            credential: {
              oauthToken: credential?.accessToken,
              oauthTokenSecret: credential?.secret,
            },
            additionalUserInfo: {
              accountId: (info?.profile?.screen_name as string)?.toLowerCase(),
              displayName: info?.profile?.name as string,
              introduction: info?.profile?.description as string,
              profileUrl: info?.profile?.profile_image_url_https as string,
            },
          }
        },
        unLinkTwitter: async () => {
          const { providerId } = new TwitterAuthProvider()
          const user = await getUser()

          if (!user) throw new Error('user not found')

          return unlink(user, providerId).then(user => transformUser(user))
        },
        /*
         * LINE ビルドを通常ブラウザで開いた場合の Twitter 連携。
         * プラグインの linkWithTwitter は内部で resolver 未登録の popup を呼び
         * auth/argument-error になるため、resolver を明示した linkWithPopup を使う。
         * サインイン中ユーザーに紐付ける処理なので signInWithPopup ではなく linkWithPopup。
         */
        linkWithPopupTwitter: async () => {
          const user = await getUser()
          if (!user) throw new Error('user not found')
          const res = await linkWithPopup(
            user,
            new TwitterAuthProvider(),
            browserPopupRedirectResolver
          )
          const credential = TwitterAuthProvider.credentialFromResult(res)
          // credential が取得できない場合エラー
          if (!credential) throw new Error('authenticationFailed')
          const transformedUser = transformUser(res.user)
          setUser(transformedUser)
          return {
            providerId: 'twitter',
            user: transformedUser,
            credential: {
              oauthToken: credential?.accessToken,
              oauthTokenSecret: credential?.secret,
            },
          }
        },
        // メアド + password ユーザー向け (詳細: docs/utoniq-app/auth.md#email-認証フロー)
        updateEmail: async (
          newEmail: string,
          password: string,
          updateOperation: (inputEmail: string) => Promise<void>
        ) => {
          const firebaseUser = getAuth().currentUser
          if (!firebaseUser?.email) {
            throw new Error('updateEmail: currentUser or email is null')
          }

          await reauthenticate(firebaseUser.email, password)
          await updateOperation(newEmail)

          const refreshedUser = getAuth().currentUser
          if (!refreshedUser) {
            // backend mutation 完走後に currentUser null は想定外。silent return すると
            // 完了 modal が出るため明示的に throw する。
            throw new Error(
              'updateEmail: currentUser null after backend mutation; Auth.email not updated'
            )
          }
          await updateAuthEmail(refreshedUser, newEmail)

          // updateAuthEmail は currentUser を in-place 更新するため再取得して state 反映。
          const updatedUser = getAuth().currentUser
          if (updatedUser) {
            setUser(transformUser(updatedUser))
          }
        },
        // SNS ログインユーザー向け (詳細: docs/utoniq-app/auth.md#sns-認証--email-紐付け)
        updateEmailSocial: async (newEmail: string) => {
          const MAX_ATTEMPTS = 2

          for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
            // 各試行で currentUser を取り直す (reauth で差し替わる可能性のため)。
            const firebaseUser = getAuth().currentUser
            if (!firebaseUser) {
              throw new Error(
                `updateEmailSocial: currentUser null at attempt ${attempt}; Auth.email not updated`
              )
            }

            try {
              await updateAuthEmail(firebaseUser, newEmail)
              break
            } catch (error) {
              const isLastAttempt = attempt === MAX_ATTEMPTS - 1
              // auth/requires-recent-login の発生条件は明記されていないため、発生したら再認証してリトライする。
              if (
                !(error instanceof FirebaseError) ||
                error.code !== 'auth/requires-recent-login' ||
                isLastAttempt
              ) {
                throw error
              }
              await reauthenticateBySocialLogin()
            }
          }

          const updatedUser = getAuth().currentUser
          if (updatedUser) {
            setUser(transformUser(updatedUser))
          }
        },
        updatePassword: async (oldPassword, newPassword) => {
          const firebaseUser = getAuth().currentUser
          if (!firebaseUser) return

          return reauthenticate(firebaseUser.email || '', oldPassword)
            .then(async () => await updatePassword(firebaseUser, newPassword))
            .catch(error => {
              throw error
            })
        },
        sendPasswordResetEmail: async (email: string) => {
          return sendPasswordResetEmail(getAuth(), email)
        },
        verifyPasswordResetCode: async (code: string) => {
          return verifyPasswordResetCode(getAuth(), code)
        },
        confirmPasswordReset: async (code: string, newPassword: string) => {
          return confirmPasswordReset(getAuth(), code, newPassword)
        },
        getRedirectResult: () => {
          return getRedirectResult(getAuth()).then(res => {
            // redirect operationではない場合userはnullなので、なにもしない
            if (!res?.user) return null

            const info = getAdditionalUserInfo(res)

            if (res.providerId === ProviderId.TWITTER) {
              const credential = TwitterAuthProvider.credentialFromResult(res)
              // credentialが取得できない場合エラー
              if (!credential) throw new Error('authenticationFailed')

              return {
                providerId: 'twitter',
                user: transformUser(res.user),
                credential: {
                  oauthToken: credential?.accessToken,
                  oauthTokenSecret: credential?.secret,
                },
                additionalUserInfo: {
                  accountId: (
                    info?.profile?.screen_name as string
                  )?.toLowerCase(),
                  displayName: info?.profile?.name as string,
                  introduction: info?.profile?.description as string,
                  profileUrl: info?.profile?.profile_image_url_https as string,
                },
              }
            }

            if (res.providerId === ProviderId.APPLE) {
              if (!info?.isNewUser) return null

              return {
                providerId: 'apple',
                user: transformUser(res.user),
                credential: null,
                additionalUserInfo: {
                  accountId: res.user.uid.toLowerCase().slice(20),
                  displayName:
                    res.user.displayName ||
                    res.user.uid.toLowerCase().slice(20),
                  introduction: '',
                  profileUrl: '',
                },
              }
            }

            // 連携対象のプロバイダーが見つからない場合エラー
            throw new Error('no provider.')
          })
        },
        /*
         * 外部標準ブラウザ（web）で Apple ログインを行う。
         * LINE ミニアプリ内では popup / redirect が使えないため、外部ブラウザ用ページ側で使う。
         * 結果の整形は getRedirectResult の Apple 整形ロジックと同じ形に揃える。
         */
        signInWithPopupApple: async () => {
          const provider = new OAuthProvider(ProviderId.APPLE)
          provider.addScope('email')
          provider.addScope('name')
          // ハイブリッド構成では Auth に popup 用リゾルバが無く auth/argument-error に
          // なるため、browserPopupRedirectResolver を明示的に渡す。
          const res = await signInWithPopup(
            getAuth(),
            provider,
            browserPopupRedirectResolver
          )
          const info = getAdditionalUserInfo(res)
          setUser(transformUser(res.user))
          return {
            providerId: 'apple',
            user: transformUser(res.user),
            credential: null,
            isNewUser: !!info?.isNewUser,
            additionalUserInfo: {
              accountId: res.user.uid.toLowerCase().slice(20),
              displayName:
                res.user.displayName || res.user.uid.toLowerCase().slice(20),
              introduction: '',
              profileUrl: '',
            },
          }
        },
        /*
         * 外部標準ブラウザ（web）で X（Twitter）ログインを行う。
         * 結果の整形は getRedirectResult の Twitter 整形ロジックと同じ形に揃える。
         */
        signInWithPopupTwitter: async () => {
          // ハイブリッド構成では Auth に popup 用リゾルバが無く auth/argument-error に
          // なるため、browserPopupRedirectResolver を明示的に渡す。
          const res = await signInWithPopup(
            getAuth(),
            new TwitterAuthProvider(),
            browserPopupRedirectResolver
          )
          const info = getAdditionalUserInfo(res)
          const credential = TwitterAuthProvider.credentialFromResult(res)
          // credential が取得できない場合エラー
          if (!credential) throw new Error('authenticationFailed')
          const transformedUser = transformUser(res.user)
          setUser(transformedUser)
          return {
            providerId: 'twitter',
            user: transformedUser,
            credential: {
              oauthToken: credential?.accessToken,
              oauthTokenSecret: credential?.secret,
            },
            isNewUser: !!info?.isNewUser,
            additionalUserInfo: {
              accountId: (info?.profile?.screen_name as string)?.toLowerCase(),
              displayName: info?.profile?.name as string,
              introduction: info?.profile?.description as string,
              profileUrl: info?.profile?.profile_image_url_https as string,
            },
          }
        },
      }
    },
    AuthProvider: ({ children }) => {
      const userInfo = useAuthUser()
      return (
        <AuthContext.Provider value={userInfo}>{children}</AuthContext.Provider>
      )
    },

    useCurrentUser: () => useContext(AuthContext),

    async getUser() {
      return getUser().then(user => transformUser(user))
    },
    async getAccessToken(opt) {
      return getUser(false)
        .then(user => user?.getIdToken(!!opt?.forceUpdateToken) || null)
        .catch(error => {
          messageRef.current?.showError(error)
          return null
        })
    },
  }
}

const useAuthUser = () => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [userClaims, setUserClaims] = useState<UserClaims | null>(null)

  useEffect(() => {
    setLoading(true)
    getUser()
      .then(user => {
        setUser(transformUser(user))
        return getUserClaims()
      })
      .then(newUserClaims => {
        setUserClaims(newUserClaims)
      })
      .catch(e => {
        setError(e)
      })
      .finally(() => setLoading(false))
  }, [])

  return { loading, user, error, setUser, userClaims, setUserClaims }
}

/**
 * Userデータをfirebaseから一般型に変更
 * @param user
 * @returns
 */
function transformUser(user: FBUser | null): User | null {
  if (!user) return null

  // Prioritize retrieving the latest data from reloadUserInfo first, then fallback to providerData
  const providers = (get(user, 'reloadUserInfo.providerUserInfo') ||
    user.providerData) as FBUser['providerData']

  return {
    uid: user.uid,
    email: user.email,
    emailVerified: user.emailVerified,
    displayName: user.displayName || '',
    avatarUrl: user.photoURL,
    phoneNumber: user.phoneNumber,
    providers: providers?.map(providerData => {
      return {
        displayName: providerData?.displayName || null,
        providerId: providerData?.providerId || null,
      }
    }),
    introduction: '',
    scope: {},
    roles: [],
    // 匿名ユーザーとしてログインしている場合はtrue
    isAnonymous: !!user?.isAnonymous,
    // 本ユーザーとしてログインしている場合はtrue
    isSigned: !!user && !user?.isAnonymous,
  }
}

function transformUserClaims(tokenResult?: IdTokenResult): UserClaims | null {
  if (!tokenResult?.claims) {
    return null
  }
  return {
    orgs: tokenResult?.claims?.orgs as
      | Record<string, { artist: boolean }>
      | undefined,
  }
}

function getUser(forceReloadUser = true) {
  return new Promise<FBUser | null>((res, rej) => {
    const unsubscribe = onAuthStateChanged(
      getAuth(),
      user => {
        unsubscribe()
        if (!user) return res(null)

        if (forceReloadUser) {
          user
            .reload()
            .then(() => {
              res(user)
            })
            .catch(err => {
              rej(err)
            })
        } else {
          res(user)
        }
      },
      err => {
        rej(err)
      }
    )
  })
}

async function getUserClaims(): Promise<UserClaims | null> {
  const currentUser = await getAuth().currentUser
  if (!currentUser) {
    return Promise.resolve(null)
  }
  const idTokenResult = await currentUser.getIdTokenResult()
  return transformUserClaims(idTokenResult)
}

async function reauthenticate(
  email: string,
  password: string
): Promise<UserCredential> {
  const user = getAuth().currentUser
  // 呼出間でセッション切れる race を防ぐため throw する (後続 sensitive op が「reauth 済」前提で進むのを防ぐ)。
  if (!user) {
    throw new Error('reauthenticate: currentUser is null')
  }

  const credentials = EmailAuthProvider.credential(email, password)
  return reauthenticateWithCredential(user, credentials)
}

async function reauthenticateBySocialLogin() {
  const user = getAuth().currentUser
  // 呼出元 (updateEmailSocial のリトライループ) が「再認証成功」と誤認するのを防ぐため throw する。
  if (!user) {
    throw new Error('reauthenticateBySocialLogin: currentUser is null')
  }

  const userProviders = user.providerData?.map(provider => provider?.providerId)

  if (userProviders.some(providerId => providerId === ProviderId.TWITTER)) {
    return reauthenticateByTwitter()
  }

  if (userProviders.some(providerId => providerId === ProviderId.APPLE)) {
    return reauthenticateByApple()
  }

  /*
   * LINEミニアプリのアカウントは providerData が空になる。
   * 静的 import は clients/utoniq-core 経由でこのファイルに戻る循環になるため動的に取り込む
   */
  if (userProviders.length === 0) {
    const { reauthenticateByLine } = await import(
      '@/line/auth/reauthenticateByLine'
    )
    return reauthenticateByLine()
  }

  // 想定外プロバイダで silent no-op すると「再認証成功」と誤認させてしまう。
  throw new Error(
    `reauthenticateBySocialLogin: unsupported providers (${
      userProviders?.join(', ') || 'none'
    })`
  )
}

async function reauthenticateByTwitter() {
  const user = getAuth().currentUser
  if (!user) {
    throw new Error('reauthenticateByTwitter: currentUser is null')
  }

  const result = await FirebaseAuthentication.signInWithTwitter({
    skipNativeAuth: false,
  })

  const credential = TwitterAuthProvider.credential(
    result.credential?.accessToken || '',
    result.credential?.secret || ''
  )

  return reauthenticateWithCredential(user, credential)
}

async function reauthenticateByApple() {
  const user = getAuth().currentUser
  if (!user) {
    throw new Error('reauthenticateByApple: currentUser is null')
  }

  const result = await FirebaseAuthentication.signInWithApple({
    skipNativeAuth: true,
    scopes: ['email', 'name'],
  })

  const provider = new OAuthProvider(ProviderId.APPLE)
  const credential = provider.credential({
    idToken: result.credential?.idToken,
    rawNonce: result.credential?.nonce,
  })

  return reauthenticateWithCredential(user, credential)
}
